'use client';

import { startTransition, useCallback, useMemo, useState } from 'react';
import { clsx } from 'clsx';

import { Avatar } from '@monoimin/atoms';

import { Modal } from '@monoimin/molecules/Modal';

import { useParamsUpdater, useSnapCarousel } from '@monoimin/hooks';

import { PaginationWrapper } from '@monoimin/molecules/pagination/PaginationWrapper';

import { ModalInterface } from '@monoimin/molecules/carousel/ModalInterface';

import { Carousel } from '@monoimin/molecules/carousel';

import { SliderCardWrapper } from '../../components/SliderCardWrapper';
import { ReviewCard } from '../../components/review-card/ReviewCard';

import type { ReviewCardProps } from '@monoimin/sb-types';

interface ReviewFullscreenCarousel {
  reviews: ReviewCardProps[];
  variant: 'slider' | 'grid';
}

export function ReviewFullscreenCarousel({
  reviews,
  variant = 'slider',
}: ReviewFullscreenCarousel) {
  const { searchParams, onChange: onChangeParams } = useParamsUpdater();

  const paramsData = useMemo(() => {
    return reviews.map((item) => {
      return item.searchParam;
    });
  }, [reviews]);

  const paramsReview = searchParams?.get('review') || '';

  const isValidParamsReview = useMemo(() => {
    return paramsData.includes(paramsReview);
  }, [paramsData, paramsReview]);

  const [modalOpen, setModalOpen] = useState(isValidParamsReview);

  const [initialIndex, setInitialIndex] = useState<number>(() => {
    return isValidParamsReview ? paramsData.indexOf(paramsReview) : 0;
  });

  const { scrollRef, activePageIndex, prev, next, goTo } = useSnapCarousel({
    initialIndex: initialIndex,
  });

  const onChangeReviewParam = useCallback(
    (param?: string) => {
      startTransition(() => {
        onChangeParams({ review: param });
      });
    },
    [onChangeParams]
  );

  const onCardClick = useCallback(
    (index: number, param?: string) => {
      startTransition(() => {
        setInitialIndex(index);
        setModalOpen(true);
        onChangeReviewParam(param);
      });
    },
    [onChangeReviewParam]
  );

  const modalContent = useMemo(() => {
    return reviews.map((review) => {
      return (
        <div
          key={review.slug + 'fullscreen-carousel-card'}
          className='snap-center flex items-center justify-center w-full min-w-[90vw] snap-always shrink-0'
        >
          <ReviewCard
            {...review}
            className='max-w-[750px] max-h-full'
            textMaxHeight={400}
          />
        </div>
      );
    });
  }, [reviews]);

  const paginationContent = useMemo(() => {
    return reviews.map((review, index) => {
      const { customer } = review;

      if (!customer) {
        return null;
      }

      const isActive = activePageIndex === index;

      return (
        <Avatar
          key={customer.image}
          src={customer.image}
          alt={customer.name}
          size={80}
          className={clsx('w-15 md:w-20 border-4 ease-in duration-200', {
            'border-[#22d5e9]': isActive,
            'cursor-pointer border-transparent': !isActive,
          })}
          onClick={() => {
            goTo(index, 'instant');
            onChangeReviewParam(review.searchParam);
          }}
        />
      );
    });
  }, [reviews, activePageIndex, onChangeReviewParam, goTo]);

  return (
    <>
      <Modal
        isOpen={modalOpen}
        setIsOpen={setModalOpen}
        panelContainerClassName='flex items-center justify-center'
      >
        <ModalInterface
          onNext={next}
          onPrev={prev}
          onClose={() => {
            setModalOpen(false);
            startTransition(() => {
              onChangeParams({ review: '' });
            });
          }}
        >
          <div
            ref={scrollRef}
            className='overflow-x-auto flex snap-x snap-mandatory scrollbar-hide relative w-[90vw] mx-auto h-[80vh]'
          >
            {modalContent}
          </div>
          <PaginationWrapper
            active={activePageIndex}
            max={reviews.length}
            className='flex gap-5 mx-auto w-fit max-w-screen px-4 mt-5'
          >
            {paginationContent}
          </PaginationWrapper>
        </ModalInterface>
      </Modal>
      {variant === 'slider' && (
        <Carousel className='-mx-5 md:mx-0' slidesCount={reviews.length}>
          {reviews.map((review, index) => {
            return (
              <SliderCardWrapper key={review.slug} withStandardHeight={false}>
                <ReviewCard
                  {...review}
                  onClick={() => onCardClick(index, review.searchParam)}
                  className='h-full w-full max-h-[500px] group hover:bg-grey cursor-pointer'
                  textClassName='group-hover:after:from-grey group-hover:after:to-grey/0'
                  textMaxHeight={300}
                  textOverflow='hidden'
                />
              </SliderCardWrapper>
            );
          })}
        </Carousel>
      )}
      {variant === 'grid' && (
        <div className='container grid grid-cols-12 gap-6 review-grid-container'>
          {reviews.map((review, index) => (
            <ReviewCard
              key={review.slug}
              {...review}
              onClick={() => onCardClick(index, review.searchParam)}
              className='h-full review-grid-item max-h-[500px] group hover:bg-grey cursor-pointer'
              textClassName='group-hover:after:from-grey group-hover:after:to-grey/0'
              textMaxHeight={300}
              textOverflow='hidden'
            />
          ))}
        </div>
      )}
    </>
  );
}
